import type { RefObject } from 'react'

import {
    gsap,
    registerGsap,
    useGSAP
} from '@/lib/gsap'

/**
 * Mouse-move parallax, recreated from the Webflow MOUSE_MOVE interaction
 * (IX2 list a-88, which drifts an element +/-4% across the pointer range).
 * Elements opt in with:
 *
 *   data-mouse-parallax        marker (required)
 *   data-mouse-parallax-x="4"  max xPercent drift (optional, default 4)
 *   data-mouse-parallax-y="0"  max yPercent drift (optional, default 0)
 */
export const useMouseParallax = (scope: RefObject<HTMLElement | null>): void => {
    useGSAP(
        () => {
            registerGsap()

            const root = scope.current
            if (!root) return

            const elements = Array.from(root.querySelectorAll<HTMLElement>('[data-mouse-parallax]'))
            if (elements.length === 0) return

            if (window.matchMedia('(hover: none)').matches) return

            const movers = elements.map(element => ({
                strengthX: Number(element.dataset.mouseParallaxX ?? element.dataset.mouseParallax ?? 4),
                strengthY: Number(element.dataset.mouseParallaxY ?? 0),
                setX: gsap.quickTo(element, 'xPercent', { duration: 0.6, ease: 'power3' }),
                setY: gsap.quickTo(element, 'yPercent', { duration: 0.6, ease: 'power3' })
            }))

            const onMove = (event: MouseEvent): void => {
                const normalisedX = 1 - (2 * event.clientX) / window.innerWidth
                const normalisedY = 1 - (2 * event.clientY) / window.innerHeight

                movers.forEach(mover => {
                    mover.setX(mover.strengthX * normalisedX)
                    mover.setY(mover.strengthY * normalisedY)
                })
            }

            window.addEventListener('mousemove', onMove)

            return () => window.removeEventListener('mousemove', onMove)
        },
        { scope }
    )
}
